import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileArrowUp, LockSimple, LockSimpleOpen } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const FACT_KEYS = new Set(["full_name", "dob", "phone", "email", "address", "city", "state", "zip",
  "emergency_name", "emergency_relationship", "emergency_phone", "payer", "insurance_info", "service_start_date"]);

const SECTIONS = [
  ["The basics", [
    ["full_name", "Client full name"], ["dob", "Date of birth", "date"], ["phone", "Phone"], ["email", "Email"],
    ["address", "Street address"], ["city", "City"], ["state", "State"], ["zip", "ZIP"],
    ["service_start_date", "Requested/actual start date", "date"],
    ["payer", "Payment source", "select", ["", "KEYSTONE FIRST CHC (TOV)", "CHC", "AmeriHealth Caritas", "PA Health & Wellness", "UPMC", "Private Pay", "Veteran's Benefit", "LTC Insurance"]],
    ["insurance_info", "Insurance / member ID info"],
  ]],
  ["Emergency & representative", [
    ["emergency_name", "Emergency contact name"], ["emergency_relationship", "Relationship"],
    ["emergency_phone", "Emergency contact phone"],
    ["has_authorized_rep", "Has POA / authorized representative?", "select", ["", "Yes", "No"]],
    ["rep_name", "Representative name"], ["rep_phone", "Representative phone"], ["rep_email", "Representative email"],
  ]],
  ["Home & safety", [
    ["lives_alone", "Lives alone?", "select", ["", "Yes", "No"]],
    ["household_members", "Who lives in the home"],
    ["pets", "Pets on premises"], ["smoking", "Smoking inside home", "select", ["", "Yes", "No"]],
    ["oxygen", "Oxygen therapy", "select", ["", "Yes", "No"]],
    ["weapons", "Unsecured weapons/firearms", "select", ["", "Yes", "No"]],
    ["minors", "Anyone under 18 in the home", "select", ["", "Yes", "No"]],
    ["contact_precautions", "Contact precautions", "select", ["", "Yes", "No"]],
    ["hazards", "Home hazards", "select", ["", "Yes", "No"]],
    ["hazard_notes", "Details for any yes answers", "textarea"],
  ]],
  ["Schedule & care", [
    ["proposed_schedule", "Proposed schedule"], ["caregiver_request", "Caregiver to train/hire"],
    ["referral_relationship", "Referral relationship"], ["caller_name", "Caller name"], ["caller_phone", "Caller phone"],
  ]],
  ["Payer / service coordinator", [
    ["payer_raw", "Payment source (as written)"], ["medicaid_id", "Medicaid ID"],
    ["sc_entity", "SC entity"], ["sc_name", "Service coordinator"], ["sc_phone", "SC phone"], ["sc_email", "SC email"],
  ]],
  ["Notes", [["notes", "Anything else about this client", "textarea"]]],
];

export default function IntakePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(id && id !== "new" ? id : "");
  const [data, setData] = useState({});
  const [savedAt, setSavedAt] = useState("");
  const [dirty, setDirty] = useState({});
  const [uploading, setUploading] = useState(false);
  const timer = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState("list");
  const [bank, setBank] = useState(null);
  const [gIdx, setGIdx] = useState(0);
  const [linkMsg, setLinkMsg] = useState("");

  const loadBank = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await api.get(`/clients/${cid}/intake-bank`);
      setBank(r.data);
    } catch (e) { /* bank unavailable — list mode still works */ }
  }, []);

  useEffect(() => { loadBank(clientId); }, [clientId, loadBank]);

  const toggleOfficeOnly = async (key, officeOnly) => {
    try {
      await api.put("/admin/intake-bank/office-only", { key, office_only: officeOnly });
      toast.success(officeOnly ? "Marked office-only — hidden from the client questionnaire" : "Client can answer this one now");
      loadBank(clientId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendToClient = async () => {
    try {
      const r = await api.post(`/clients/${clientId}/intake-link`, { send: true });
      try { await navigator.clipboard.writeText(r.data.url); } catch (e) { /* clipboard blocked */ }
      setLinkMsg(r.data.url);
      toast.success(r.data.emailed_to ? `Questionnaire emailed to ${r.data.emailed_to} — link copied too` : "Link created and copied (no email on file yet)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [start, setStart] = useState({ full_name: "", phone: "", email: "", caller_name: "" });
  const [starting, setStarting] = useState(false);
  const startClient = async () => {
    if (!start.full_name.trim()) return;
    setStarting(true);
    try {
      const r = await api.post("/clients", { intake: Object.fromEntries(Object.entries(start).filter(([, v]) => v.trim())) });
      toast.success("Client created — now answer questions yourself or send them the questionnaire");
      navigate(`/intake-form/${r.data.id}`, { replace: true });
      setClientId(r.data.id);
      loadBank(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStarting(false); }
  };

  useEffect(() => {
    if (!id || id === "new") return;
    (async () => {
      try {
        const [facts, q] = await Promise.all([
          api.get(`/clients/${id}/facts`),
          api.get(`/clients/${id}/intake-questionnaire`),
        ]);
        const merged = {};
        Object.entries(facts.data.facts || {}).forEach(([k, v]) => { if (v.value) merged[k] = v.value; });
        Object.entries(q.data.questionnaire || {}).forEach(([k, v]) => {
          if (typeof v === "string" && v && !merged[k]) merged[k] = v;
        });
        setData(merged);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [id]);

  const flush = useCallback(async (pending) => {
    const keys = Object.keys(pending);
    if (!keys.length) return;
    try {
      let cid = clientId;
      const rest = { ...pending };
      if (!cid) {
        if (!pending.full_name) return; // need a name before the record exists
        const res = await api.post("/clients", { intake: { full_name: pending.full_name } });
        cid = res.data.id;
        setClientId(cid);
        navigate(`/intake-form/${cid}`, { replace: true });
        loadBank(cid);
        delete rest.full_name; // already written by create
      }
      if (Object.keys(rest).length) await api.put(`/clients/${cid}/intake-bank/answers`, { answers: rest });
      setSavedAt(new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }));
      setDirty({});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [clientId, navigate]);

  const change = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setDirty((d) => {
      const next = { ...d, [key]: value };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => flush(next), 900);
      return next;
    });
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/intake/prefill-upload", fd, { timeout: 120000 });
      const facts = res.data.facts || {};
      const blanks = Object.entries(facts).filter(([k, v]) => v && !data[k]);
      if (!blanks.length) {
        toast.info("Everything in that document is already filled in here");
        return;
      }
      const pending = {};
      blanks.forEach(([k, v]) => { pending[k] = v; });
      setData((d) => ({ ...d, ...pending }));
      setDirty((d) => ({ ...d, ...pending }));
      await flush(pending);
      toast.success(`Prefilled ${blanks.length} field${blanks.length === 1 ? "" : "s"} from ${f.name} — review before continuing`);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6" data-testid="intake-page">
      <button onClick={() => navigate("/clients")} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="intake-back">
        <ArrowLeft size={14} /> Clients
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Client intake</h1>
          <p className="mt-1 text-sm text-stone-500">
            Answer straight through — it saves as you go. Stop anytime; come back to this same page to continue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={onUpload} data-testid="intake-upload-input" />
          <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="intake-prefill-upload">
            <FileArrowUp size={16} className="mr-1" /> {uploading ? "Reading…" : "Prefill from a document"}
          </Button>
          <Button variant={mode === "guided" ? "default" : "outline"} className="rounded-full" onClick={() => setMode(mode === "guided" ? "list" : "guided")} data-testid="intake-guided-toggle">
            {mode === "guided" ? "List view" : "Guided — one at a time"}
          </Button>
          {clientId && (
            <Button variant="outline" className="rounded-full" onClick={sendToClient} data-testid="intake-send-to-client">
              Send questionnaire to client
            </Button>
          )}
        </div>
      </div>
      {linkMsg && (
        <p className="mt-2 break-all rounded-lg border border-[#2E7D32]/30 bg-[#2E7D32]/5 px-3 py-2 text-xs text-[#2E7D32]" data-testid="intake-link-url">{linkMsg}</p>
      )}
      {bank && (
        <p className="mt-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-600" data-testid="intake-readiness">
          {bank.readiness.answered} of {bank.readiness.total} bank questions answered · Agreement packet:{" "}
          {bank.readiness.agreement_ready
            ? <b className="text-[#2E7D32]">ready to print, fully prefilled</b>
            : <span>missing <b>{bank.readiness.agreement_missing.join(", ")}</b></span>}
        </p>
      )}
      <p className="mt-2 text-xs text-stone-400" data-testid="intake-save-status">
        {dirtyCount ? "Saving…" : savedAt ? `All changes saved · ${savedAt}` : "Nothing to save yet"}
      </p>

      {!clientId && (
        <div className="mt-8 max-w-xl rounded-2xl border border-stone-200 bg-white p-7 shadow-sm" data-testid="intake-start-card">
          <h2 className="font-heading text-xl font-semibold text-stone-900">Start a new client</h2>
          <p className="mt-1 text-xs leading-relaxed text-stone-500">
            The minimum to get going. Name is enough to save — but add phone + email so you can send them
            the questionnaire right away. Everything else comes after.
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Client's full name *</label>
              <input value={start.full_name} onChange={(e) => setStart((s) => ({ ...s, full_name: e.target.value }))}
                className="h-11 w-full rounded-xl border border-stone-300 px-3" data-testid="intake-start-name" autoFocus />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-stone-600">Phone</label>
                <input value={start.phone} onChange={(e) => setStart((s) => ({ ...s, phone: e.target.value }))}
                  className="h-11 w-full rounded-xl border border-stone-300 px-3" data-testid="intake-start-phone" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-stone-600">Email <span className="font-normal text-stone-400">(needed to send the questionnaire)</span></label>
                <input type="email" value={start.email} onChange={(e) => setStart((s) => ({ ...s, email: e.target.value }))}
                  className="h-11 w-full rounded-xl border border-stone-300 px-3" data-testid="intake-start-email" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Who's calling? <span className="font-normal text-stone-400">(optional — if not the client)</span></label>
              <input value={start.caller_name} onChange={(e) => setStart((s) => ({ ...s, caller_name: e.target.value }))}
                className="h-11 w-full rounded-xl border border-stone-300 px-3" data-testid="intake-start-caller" />
            </div>
            <Button onClick={startClient} disabled={starting || !start.full_name.trim()}
              className="h-11 rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="intake-start-create">
              {starting ? "Creating…" : "Create & open intake"}
            </Button>
          </div>
        </div>
      )}

      {clientId && mode === "guided" && (() => {
        let qs = bank?.questions?.length
          ? bank.questions
          : [{ key: "full_name", label: "Client's full name", type: "text", section: "About the client" }];
        const showOkG = (qq) => {
          const aud = qq.audience || "everyone";
          const resp = String(data.respondent || "").trim();
          if (aud === "family" && resp && resp !== "A family member or representative") return false;
          if (aud === "client" && resp && resp !== "The client") return false;
          const si = qq.show_if;
          if (!si) return true;
          const raw = data[si.key];
          if (si.contains) return Array.isArray(raw) && raw.includes(si.contains);
          if (si.contains_any) return Array.isArray(raw) && si.contains_any.some((x) => raw.includes(x));
          const val = String(raw || "").trim();
          if (si.not_in) return val !== "" && !si.not_in.includes(val);
          if (si.in) return si.in.includes(val);
          return true;
        };
        qs = qs.filter(showOkG);
        const gi = Math.min(gIdx, qs.length - 1);
        const q = qs[gi];
        return (
          <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-7 shadow-sm" data-testid="intake-guided-card">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{q.section}</p>
              <p className="text-xs text-stone-400" data-testid="guided-progress">{gi + 1} of {qs.length}</p>
            </div>
            <div className="flex items-start justify-between gap-3">
              <label className="font-heading text-xl font-semibold leading-snug text-stone-900" data-testid="guided-question-label">{q.label}</label>
              {bank && (
                <button onClick={() => toggleOfficeOnly(q.key, !q.office_only)}
                  title={q.office_only ? "Office answers this — click to let clients answer it" : "Clients answer this — click to make it office-only"}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold transition-colors ${q.office_only ? "border-[#4A235A]/40 bg-[#4A235A]/10 text-[#4A235A]" : "border-stone-300 text-stone-400 hover:text-stone-600"}`}
                  data-testid={`guided-officelock-${q.key}`}>
                  {q.office_only ? <><LockSimple size={11} weight="fill" /> office-only</> : <><LockSimpleOpen size={11} /> client answers</>}
                </button>
              )}
            </div>
            <div className="mt-4">
              {q.type === "select" ? (
                <div className="flex flex-wrap gap-2" data-testid="guided-options">
                  {(q.options || []).filter(Boolean).map((o) => (
                    <button key={o} type="button" onClick={() => change(q.key, o)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${(data[q.key] || "") === o ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 bg-white text-stone-700 hover:border-[#4A235A]"}`}
                      data-testid={`guided-option-${o.replace(/\W+/g, "-").toLowerCase()}`}>{o}</button>
                  ))}
                </div>
              ) : q.type === "textarea" ? (
                <textarea value={data[q.key] || ""} onChange={(e) => change(q.key, e.target.value)} rows={4}
                  className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm" data-testid={`guided-input-${q.key}`} />
              ) : q.type === "multiselect" ? (
                <div className="flex flex-wrap gap-2" data-testid={`guided-input-${q.key}`}>
                  {(q.options || []).map((o) => {
                    const arr = Array.isArray(data[q.key]) ? data[q.key] : [];
                    const on = arr.includes(o);
                    return (
                      <button key={o} type="button" onClick={() => change(q.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 bg-white text-stone-600 hover:border-[#4A235A]"}`}
                        data-testid={`guided-option-${o.replace(/\W+/g, "-").toLowerCase()}`}>
                        {on ? "✓ " : ""}{o}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <input type={q.type === "date" ? "date" : "text"} value={data[q.key] || ""} onChange={(e) => change(q.key, e.target.value)}
                  className="h-12 w-full rounded-xl border border-stone-300 bg-white px-4 text-base" data-testid={`guided-input-${q.key}`} />
              )}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <button onClick={() => setGIdx(Math.max(0, gi - 1))} disabled={gi === 0}
                className="text-sm text-stone-400 transition-colors hover:text-stone-700 disabled:opacity-0" data-testid="guided-back">← Back</button>
              <Button onClick={() => setGIdx(Math.min(qs.length - 1, gi + 1))}
                className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="guided-next">
                {gi >= qs.length - 1 ? "Done" : "Next"}
              </Button>
            </div>
          </div>
        );
      })()}

      {clientId && mode === "list" && bank && Object.entries(
        bank.questions.filter((q) => {
          const si = q.show_if;
          if (!si) return true;
          const raw = data[si.key];
          if (si.contains) return Array.isArray(raw) && raw.includes(si.contains);
          if (si.contains_any) return Array.isArray(raw) && si.contains_any.some((x) => raw.includes(x));
          const val = String(raw || "").trim();
          if (si.not_in) return val !== "" && !si.not_in.includes(val);
          if (si.in) return si.in.includes(val);
          return true;
        }).reduce((acc, q) => { (acc[q.section] = acc[q.section] || []).push(q); return acc; }, {})
      ).map(([title, qs]) => (
        <div key={title} className="mt-8" data-testid={`intake-section-${title.replace(/\W+/g, "-").toLowerCase()}`}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-stone-400">
            {title} <span className="ml-1 rounded-full bg-stone-100 px-2 py-0.5 text-[9px] font-bold text-stone-400 normal-case">phase {qs[0].phase}</span>
          </p>
          <div className="grid grid-cols-1 gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm md:grid-cols-2">
            {qs.map((q) => (
              <div key={q.key} className={q.type === "textarea" || q.type === "multiselect" ? "md:col-span-2" : ""}>
                <div className="flex items-center justify-between gap-2">
                  <label className="text-xs font-medium text-stone-600">{q.label}
                    {bank.answer_meta?.[q.key] && (
                      <span className="ml-1.5 text-[9px] font-normal text-stone-400" data-testid={`intake-meta-${q.key}`}>
                        · {bank.answer_meta[q.key].by === "client" ? "client" : bank.answer_meta[q.key].by} · {String(bank.answer_meta[q.key].at || "").slice(0, 10)}
                      </span>
                    )}
                  </label>
                  <button onClick={() => toggleOfficeOnly(q.key, !q.office_only)}
                    className={`mb-1 inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold transition-colors ${q.office_only ? "border-[#4A235A]/40 bg-[#4A235A]/10 text-[#4A235A]" : "border-stone-200 text-stone-300 hover:text-stone-500"}`}
                    data-testid={`list-officelock-${q.key}`}>
                    {q.office_only ? <><LockSimple size={9} weight="fill" /> office-only</> : <><LockSimpleOpen size={9} /> client answers</>}
                  </button>
                </div>
                {q.type === "select" ? (
                  <select value={data[q.key] || ""} onChange={(e) => change(q.key, e.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm"
                    data-testid={`intake-${q.key}`}>
                    {(q.options || []).map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                  </select>
                ) : q.type === "textarea" ? (
                  <textarea value={data[q.key] || ""} onChange={(e) => change(q.key, e.target.value)} rows={3}
                    className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm"
                    data-testid={`intake-${q.key}`} />
                ) : q.type === "multiselect" ? (
                  <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`intake-${q.key}`}>
                    {(q.options || []).map((o) => {
                      const arr = Array.isArray(data[q.key]) ? data[q.key] : [];
                      const on = arr.includes(o);
                      return (
                        <button key={o} type="button"
                          onClick={() => change(q.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${on ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 bg-white text-stone-600 hover:border-[#4A235A]"}`}
                          data-testid={`intake-${q.key}-${o.replace(/\W+/g, "-").toLowerCase()}`}>
                          {on ? "✓ " : ""}{o}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <input type={q.type || "text"} value={data[q.key] || ""} onChange={(e) => change(q.key, e.target.value)}
                    className="mt-1 h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm"
                    data-testid={`intake-${q.key}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {clientId && (
        <div className="mt-10 rounded-xl border border-[#2F6B4F]/25 bg-[#2F6B4F]/[0.04] p-5" data-testid="intake-generate">
          <p className="text-sm font-semibold text-stone-900">Complete? Generate the signature forms.</p>
          <p className="mt-1 text-xs text-stone-500">Everything you entered is already on the client's record — the agreement prefills from it.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => navigate(`/clients/${clientId}/service-agreement`)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-open-agreement">
              Review & open Service Agreement
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => navigate(`/clients/${clientId}`)} data-testid="intake-open-client">
              Open client page
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
